import Fuse from 'fuse.js';
import React, { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Avatar, Button, Buttons, CancelButton, Checkbox, Icon, ModalCard, SearchInput, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useForm } from '~/hooks';
import { colors } from '~/styles';
import { sortAlphabetically } from '~/utils';

const S = {
  Table: styled(Table)`
    font-size: 0.875rem;
    margin-top: 1rem;
    max-height: 25rem;
    overflow-y: auto;
  `,

  Checkbox: styled.div`
    > label > div {
      background: ${colors.white};
      width: 1.125rem;
      height: 1.125rem;
      font-size: 0.5rem;
      margin: 0;
    }
  `,

  Header: styled(Table.Header)`
    ${({ sticky }) =>
      sticky &&
      css`
        position: sticky;
        top: 0;
        z-index: 1;
      `}
  `,

  MemberInfo: styled.div`
    display: flex;
    align-items: center;
    opacity: ${({ $inactive }) => ($inactive ? 0.4 : 1)};
  `,

  MemberName: styled.span`
    margin-left: 0.5rem;
  `,

  TableState: styled.div`
    position: sticky;
    left: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    color: ${({ $error }) => ($error ? colors.danger : colors.grey40)};
    font-size: 0.875rem;
  `,
};

export default function ProjectRoleMembersModal({ project, role, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();

  const [projectMembers, setProjectMembers] = useState(null);
  const [hasLoadError, setHasLoadError] = useState(false);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).members().assignable();
        setProjectMembers(data);
      } catch {
        setHasLoadError(true);
      }
    })();
  }, [api, workspace.id, project.id]);

  const [selectedIds, setSelectedIds] = useState(() => new Set(role.members.map((pmr) => pmr.projectMember.id)));

  const [searchQuery, setSearchQuery] = useState('');
  const options = useMemo(() => {
    const members = sortAlphabetically(projectMembers ?? [], [(pm) => (pm.isActuallyActive ? 0 : 1), 'member.name']);
    if (!searchQuery) return members;
    return new Fuse(members, { keys: ['member.name'], threshold: 0.4 }).search(searchQuery).map(({ item }) => item);
  }, [projectMembers, searchQuery]);

  const allVisibleSelected = options.length > 0 && options.every((pm) => selectedIds.has(pm.id));
  const someVisibleSelected = options.some((pm) => selectedIds.has(pm.id));

  const handleToggle = (projectMember) => {
    setSelectedIds((ids) => {
      const next = new Set(ids);
      if (next.has(projectMember.id)) {
        next.delete(projectMember.id);
      } else {
        next.add(projectMember.id);
      }
      return next;
    });
  };

  const handleBulkSelectionChange = () => {
    setSelectedIds((ids) => {
      const next = new Set(ids);
      if (allVisibleSelected) {
        for (const pm of options) next.delete(pm.id);
      } else {
        for (const pm of options) next.add(pm.id);
      }
      return next;
    });
  };

  const [{ isSubmitting }, form] = useForm();

  const handleSubmit = async () => {
    form.submit();

    try {
      const members = projectMembers.filter((pm) => selectedIds.has(pm.id)).map((pm) => ({ projectMemberId: pm.id }));
      await api.www.workspaces(workspace.id).projectSettings(project.id).projectRoles(role.id).patch({ members });
      toast.success('Project Role saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
    }

    form.done();
  };

  return (
    <ModalCard title="Edit Member Assignments" byline={role.name} onClose={onClose}>
      <ModalCard.Body style={{ marginBottom: 0 }}>
        <SearchInput
          value={searchQuery}
          placeholder="All"
          materialPlaceholder="Search"
          materialAlwaysVisible
          onChange={({ target: { value } }) => setSearchQuery(value)}
        />

        <S.Table>
          <S.Header sticky>
            <Table.Column align="center" width="3rem">
              <S.Checkbox>
                <Checkbox
                  aria-label="Select all members"
                  disabled={options.length === 0}
                  checked={someVisibleSelected}
                  partial={someVisibleSelected && !allVisibleSelected}
                  onChange={handleBulkSelectionChange}
                />
              </S.Checkbox>
            </Table.Column>
            <Table.Column>Member</Table.Column>
          </S.Header>

          <Table.Body showNoResults={!!projectMembers && !hasLoadError}>
            {!projectMembers && !hasLoadError ? (
              <Table.Row>
                <S.TableState>
                  <Icon icon="spinner" spin />
                  Loading project team members...
                </S.TableState>
              </Table.Row>
            ) : hasLoadError ? (
              <Table.Row>
                <S.TableState $error role="alert">
                  Unable to load the project team.
                </S.TableState>
              </Table.Row>
            ) : (
              options.map((projectMember) => (
                <Table.Row key={projectMember.id}>
                  <Table.Cell>
                    <S.Checkbox>
                      <Checkbox
                        aria-label={`Select ${projectMember.member.name}`}
                        checked={selectedIds.has(projectMember.id)}
                        onChange={() => handleToggle(projectMember)}
                      />
                    </S.Checkbox>
                  </Table.Cell>
                  <Table.Cell>
                    <S.MemberInfo $inactive={!projectMember.isActuallyActive}>
                      <Avatar
                        value={projectMember.member}
                        isCircle
                        hasBackground
                        initialsFontSize=".7rem"
                        size="1.5rem"
                      />
                      <S.MemberName>{projectMember.member.name}</S.MemberName>
                    </S.MemberInfo>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </S.Table>
      </ModalCard.Body>

      <ModalCard.Footer>
        <Buttons align="right">
          <CancelButton onClick={onClose}>Close</CancelButton>

          <Button disabled={!projectMembers} isLoading={isSubmitting} onClick={handleSubmit}>
            Save &amp; Close
          </Button>
        </Buttons>
      </ModalCard.Footer>
    </ModalCard>
  );
}
